import { useState } from 'react';
import { Button, Card, Collapse, Group, Image, SimpleGrid, Stack, Text } from '@mantine/core';
import { IconPhotoCheck } from '@tabler/icons-react';
import {
  BRAND_KIT_COPY,
  FACTORY_DELIVERABLE_SLOTS,
  TV_PLATFORM_KEYS,
  type FactoryAssetPlatform,
  type TvPlatformKey,
} from '@/lib/constants/appFactory';
import { useFactoryAssetSets } from '../useAppFactory';
import { BrandKitUtils } from '../BrandKitUtils';
import { TvDevicePreviews } from './TvDevicePreviews';
import { RokuScreenPreviews } from './RokuScreenPreviews';

interface AppCurrentImagesProps {
  appCfgId: number;
  platform: FactoryAssetPlatform;
  /** The TV platforms that are on for the app: the TV screens drawn follow them. Absent = all. */
  tv?: readonly TvPlatformKey[];
}

/**
 * The LIVE deliverable set of one platform (the serving location builds and
 * devices load from) — console-era apps included, which is why this reads the
 * factory's currentImages instead of any dashboard-side upload state. The
 * names come from img/; the URLs from the approved sets (BrandKitUtils.liveImages).
 * Platform-generic since 3.34.0: Fire TV's set also carries the store-listing
 * art the holder downloads from here for the developer console. Degrades to
 * nothing when the factory gateway is off (matching the Brand Kit card).
 */
export function AppCurrentImages({ appCfgId, platform, tv = TV_PLATFORM_KEYS }: AppCurrentImagesProps) {
  const assetSetsQuery = useFactoryAssetSets(appCfgId, platform);
  const [onTv, setOnTv] = useState(false);
  if (assetSetsQuery.isError) return null;

  // Each file from the set that last published it, never the same-name img/
  // URL the CDN may still hold from before an approval (3.36.1).
  const current = BrandKitUtils.liveImages(assetSetsQuery.data?.assetSets ?? [], assetSetsQuery.data?.currentImages ?? []);
  if (current.length === 0) return null;

  const slots = BrandKitUtils.slotsFor(FACTORY_DELIVERABLE_SLOTS[platform], tv);
  const labelByName = new Map(slots.map((s) => [s.name, s.label]));
  // A file only a platform that is off shows (the Google TV circle layers on a
  // Fire-TV-only app) is part of the set but not of what this customer manages.
  const hidden = new Set(FACTORY_DELIVERABLE_SLOTS[platform].filter((s) => !slots.includes(s)).map((s) => s.name));
  const shown = current.filter((img) => !hidden.has(img.name));

  return (
    <Card withBorder radius="md" p="md">
      <Stack gap="md">
        <Group gap="xs">
          <IconPhotoCheck size={20} />
          <Text fw={600} size="md">
            Current Images
          </Text>
        </Group>
        <Text size="sm" c="dimmed">
          {BRAND_KIT_COPY.currentIntro.replace('{platforms}', BrandKitUtils.scopeLabel(platform, tv))}
        </Text>
        <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="sm">
          {shown.map((img) => (
            <Stack key={img.name} gap={4}>
              <Image src={img.url} alt={img.name} radius="sm" h={80} fit="contain" bg="gray.1" />
              <Text size="xs" c="dimmed" ta="center" truncate>
                {labelByName.get(img.name) ?? img.name}
              </Text>
            </Stack>
          ))}
        </SimpleGrid>
        <Stack gap="xs">
          <Button variant="subtle" size="compact-sm" w="fit-content" onClick={() => setOnTv((v) => !v)}>
            {onTv ? 'Hide the TV screens' : 'See it on a TV'}
          </Button>
          <Collapse in={onTv}>
            {onTv &&
              (platform === 'roku' ? (
                <RokuScreenPreviews files={BrandKitUtils.previewFileMap(null, current) ?? {}} />
              ) : (
                <TvDevicePreviews
                  files={BrandKitUtils.previewFileMap(null, current) ?? {}}
                  show={{ firetv: tv.includes('firetv'), googletv: tv.includes('googletv') }}
                />
              ))}
          </Collapse>
        </Stack>
      </Stack>
    </Card>
  );
}
